import { acceptHMRUpdate, defineStore } from "pinia";
import { OneTrustCookieGroup } from "~/assets/constants/cookie-consent";

export const useRootStore = defineStore(
  "rootStore",
  () => {
    const locale_strings = ref({});
    const access_token = ref(null);
    const is_page_loading = ref(false);
    const enableOnetrust = ref(true);
    const authenticatedCustomer = ref(null);
    const device_id = ref(null);
    const browser = ref(null);
    const session_id = ref(null);
    const access_token_interval_id = ref<ReturnType<typeof setInterval> | null>(
      null
    );
    const toast_ids = ref([]);
    const isAppHydrated = ref(false);
    const isIncVat = ref(true);
    const after_auth_intended_route = ref<string | null>(null);
    const ecom_error = ref({});
    const buy_now_source_route = ref("");
    const is_ecom_failed_modal_visible = ref(false);
    const is_ecom_failed_modal_force_closed = ref(false);
    const ecom_failed_modal = ref(false);
    const page_background_color = ref<string | null>("#FFFFFF");
    const isReleaseModalVisible = ref(false);
    const footer_links = ref([]);
    const header_links = ref([]);
    const siteBaseURL =
      process.env.NUXT_PUBLIC_SITE_URL || "https://ts-eu24-develop.ventrox.com";
    const post_auth = ref({ callback: null });
    const debugPanel = ref(false);
    const debugPanelOpen = ref(false);

    const consentCookie = useCookie("OptanonConsent");

    const isAuthenticated = computed(() => !!authenticatedCustomer.value);
    const getSessionId = computed(() => session_id.value);

    async function processResponse(response) {
      const { processResponse } = await import(
        "~/stores-datalayer/root.datalayer"
      );
      return await processResponse(response);
    }

    async function toastSuccess(messages: string[], position = "top-center") {
      const { toastSuccess } = await import(
        "~/services-optimized/toastService"
      );
      return await toastSuccess(messages, position);
    }

    async function toastErrors(errors: string[], position = "top-center") {
      const { toastErrors } = await import("~/services-optimized/toastService");
      return await toastErrors(errors, position);
    }

    function clone(source: any) {
      return JSON.parse(JSON.stringify(source));
    }

    function setPostAuthCallback(newCallback) {
      post_auth.value.callback = newCallback;
    }

    function executePostAuthCallback() {
      if (
        post_auth.value.callback &&
        typeof post_auth.value.callback === "function"
      ) {
        post_auth.value.callback();
      }
      post_auth.value = {
        callback: null,
      };
    }

    /**
     * @param {OneTrustCookieGroup} cookieGroupId - Example: "C0001" - the category code for "strictly_necessary" cookies
     * @description Checks if a specific cookie group has been consented to or rejected.
     */
    function checkConsent(cookieGroupId: OneTrustCookieGroup) {
      if (!enableOnetrust.value) return true;
      if (!consentCookie.value) return false;
      const decodedData = extractConsentGroups(consentCookie.value);
      if (!decodedData) return false;
      return decodedData.acceptedGroups.includes(cookieGroupId);
    }

    return {
      locale_strings,
      access_token,
      is_page_loading,
      enableOnetrust,
      authenticatedCustomer,
      device_id,
      browser,
      session_id,
      access_token_interval_id,
      toast_ids,
      isAppHydrated,
      isIncVat,
      after_auth_intended_route,
      ecom_error,
      buy_now_source_route,
      is_ecom_failed_modal_visible,
      is_ecom_failed_modal_force_closed,
      ecom_failed_modal,
      page_background_color,
      isReleaseModalVisible,
      footer_links,
      header_links,
      siteBaseURL,
      post_auth,
      debugPanel,
      debugPanelOpen,
      isAuthenticated,
      getSessionId,
      processResponse,
      toastSuccess,
      toastErrors,
      clone,
      setPostAuthCallback,
      executePostAuthCallback,
      checkConsent,
    };
  },
  {
    persist: [
      {
        pick: ["after_auth_intended_route"],
      },
      {
        key: "vat",
        pick: ["isIncVat"],
      },
    ],
  }
);

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useRootStore, import.meta.hot));
}
